import { evalOps, type OpsNode } from './ops';
import { formatCount, formatSeconds } from './format';

/** 1 秒 ≒ 10^8 回（§5.2）。 */
export const OPS_PER_SECOND = 1e8;
/** 区分の境界（§5.2）。 */
export const FITS_MAX_OPS = 2e8;
export const BORDERLINE_MAX_OPS = 2e9;
/** これを超えるサイズは送らない（§5.3）。 */
export const TOO_SLOW_OPS = 1e10;

export type Band = 'fits' | 'borderline' | 'exceeds';

/** 区分（§5.2）。 */
export function band(ops: number): Band {
  if (ops <= FITS_MAX_OPS) return 'fits';
  if (ops <= BORDERLINE_MAX_OPS) return 'borderline';
  return 'exceeds';
}

/** 区分の表示文言（§5.2）。 */
export const BAND_LABELS: Record<Band, string> = {
  fits: '制限時間 2 秒に収まる見込み',
  borderline: '書き方しだいで収まらないおそれ',
  exceeds: '制限時間に収まらない見込み',
};

/** 見積もり秒数 = ops / 1e8（§5.2）。 */
export function opsToSeconds(ops: number): number {
  return ops / OPS_PER_SECOND;
}

export interface Prediction {
  ops: number;
  seconds: number;
  band: Band;
  countText: string;
  secondsText: string;
}

/** 変数の値から見積もりを作る（N スライダーもこれを使う）。 */
export function predict(
  expr: string | OpsNode,
  vars: Readonly<Record<string, number>>,
): Prediction {
  const ops = evalOps(expr, vars);
  const seconds = opsToSeconds(ops);
  return {
    ops,
    seconds,
    band: band(ops),
    countText: formatCount(ops),
    secondsText: formatSeconds(seconds),
  };
}

/** 制約の max を代入した見積もり（§5.1）。 */
export function predictFromConstraints(
  expr: string,
  constraints: readonly { name: string; max: number }[],
): Prediction {
  const vars: Record<string, number> = {};
  for (const c of constraints) vars[c.name] = c.max;
  return predict(expr, vars);
}

/** 2 つの ops の比（大きい方 ÷ 小さい方。§5.2 末尾）。どちらかが 0 以下なら null。 */
export function opsRatio(a: number, b: number): number | null {
  if (a <= 0 || b <= 0) return null;
  return Math.max(a, b) / Math.min(a, b);
}
